import React, { createContext, useContext, useEffect, useState } from 'react';
import { createConsumer } from '@rails/actioncable';

/**
 * Replacement for the abandoned `react-actioncable-provider`.
 *
 * `react-actioncable-provider@1.0.3` depended on the deprecated standalone
 * `actioncable` npm package, whose client did not understand the `disconnect`
 * message type that Rails 6.1 Action Cable servers send (on deploy/restart,
 * idle timeout, remote disconnect). Those frames have no `identifier`, so the
 * old client fell through to `notify(undefined, "received", ...)` and threw
 * `Cannot read properties of undefined (reading 'received')`. `@rails/actioncable`
 * handles the `disconnect` type, so swapping the client removes that crash.
 */
const CableContext = createContext(null);

export function CableProvider({ url, children }) {
  // The consumer holds an open WebSocket, so create it in an effect and tear it
  // down when the url changes or the provider unmounts.
  const [consumer, setConsumer] = useState(null);

  useEffect(() => {
    if (!url) return undefined;

    const newConsumer = createConsumer(url);
    setConsumer(newConsumer);

    return () => {
      newConsumer.disconnect();
      setConsumer(null);
    };
  }, [url]);

  return (
    <CableContext.Provider value={consumer}>
      {children}
    </CableContext.Provider>
  );
}

/** Returns the current Action Cable consumer, or null before it is ready. */
export function useConsumer() {
  return useContext(CableContext);
}

export default CableContext;
